import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import { ArrowDown, Trash2, ShieldCheck } from "lucide-react";
import { scrollTo } from "./Nav";

const HERO_IMG = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 60, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 60, damping: 20 });
  const px = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 50, damping: 20 });
  const py = useSpring(useTransform(my, [-0.5, 0.5], [-10, 10]), { stiffness: 50, damping: 20 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" data-testid="hero-section" onMouseMove={onMove}
      className="relative min-h-screen flex items-center overflow-hidden grain pt-28 pb-16">
      <div className="absolute -top-40 -right-40 w-[38rem] h-[38rem] rounded-full bg-forest/50 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 -left-52 w-[30rem] h-[30rem] rounded-full bg-terracotta/15 blur-[140px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-terracotta mb-8"
            data-testid="hero-overline"
          >
            Valet trash service
          </motion.p>

          <h1 className="font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl" data-testid="hero-headline">
            <MaskedLine delay={0.25}>Doorstep Trash</MaskedLine>
            <MaskedLine delay={0.38}>Pickup for <span className="text-stroke">Building</span></MaskedLine>
            <MaskedLine delay={0.51}><span className="text-terracotta">Residents.</span></MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-7 max-w-lg text-base sm:text-lg text-mist font-light leading-relaxed"
            data-testid="hero-subheadline"
          >
            The premier valet collection service built for our 500-unit community.
            Set your tied bags out during your window — we sweep the hallways clean.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button data-testid="hero-cta-pricing" onClick={() => scrollTo("#pricing")}
              className="group flex items-center gap-3 bg-terracotta text-parchment font-semibold text-sm px-7 py-4 rounded-full hover:bg-parchment hover:text-charcoal transition-colors duration-300">
              View Plans & Pricing
              <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <button data-testid="hero-cta-how" onClick={() => scrollTo("#how-it-works")}
              className="text-sm font-medium text-mist hover:text-parchment border border-white/10 hover:border-terracotta/50 px-7 py-4 rounded-full transition-colors duration-300">
              How it works
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-12 flex items-center gap-3 text-dim"
            data-testid="hero-insurance-chip"
          >
            <ShieldCheck className="w-4 h-4 text-fern" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em]">$1M / $2M General Liability Insured</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
          className="lg:col-span-5 relative hidden lg:block"
          style={{ perspective: 1000 }}
        >
          <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative" data-testid="hero-building-card">
            <div className="rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60">
              <motion.img
                src={HERO_IMG}
                alt="Modern apartment building facade"
                className="w-full h-[30rem] object-cover scale-110"
                style={{ x: px, y: py }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-transparent to-transparent" />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.15 }}
              className="absolute -bottom-6 -left-8 glass rounded-2xl px-6 py-5 animate-float-slow"
              data-testid="hero-occupancy-badge"
            >
              <p className="font-display font-extrabold text-3xl text-parchment">500</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-mist mt-1">Units serviced nightly</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: -24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3 }}
              className="absolute -top-5 -right-4 bg-terracotta rounded-full px-5 py-3 flex items-center gap-2 shadow-lg shadow-terracotta/30"
              data-testid="hero-ondemand-badge"
            >
              <Trash2 className="w-4 h-4 text-charcoal" strokeWidth={2.4} />
              <span className="text-charcoal text-xs font-bold">$4 / bag on-demand</span>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
